import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn} from "@angular/forms";
import moment from "moment";

export type ProgressMode = 'auto' | 'weight-based' | 'manual';
export type EventProgressMode = 'auto' | 'manual';

export class ProgressUtil {

  static withManualProgressValidation<T extends FormGroup>(form: T): T {
    form.addValidators(this.manualProgressValidator());
    return form;
  }

  static manualProgressValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const progressMode = control.get('progressMode')?.value;
      const progress = control.get('progress')?.value;

      if (progressMode === 'manual' && (progress == null || progress === '')) {
        return {manualProgressRequired: true};
      }

      return null;
    };
  }

  static buildGroupWeightsArray(workstream: any, fb: FormBuilder): FormArray {
    return fb.array((workstream.roadmapGroupWeights || []).map(group => fb.group({
      id: [group.id],
      name: [group.name],
      weight: [group.weight],
    })));
  }

  static setWorkstreamProgressMode(workstreams: FormArray, i: number, mode: ProgressMode): void {
    const workstreamControl = workstreams.at(i) as FormGroup;
    workstreamControl.get('progressMode').setValue(mode);
    if (mode === 'manual' && (workstreamControl.get('progress')?.value == null || workstreamControl.get('progress')?.value === '')) {
      workstreamControl.get('progress')?.setValue(0);
    }
    workstreamControl.updateValueAndValidity();
  }

  static getGroupWeightsArray(workstreams: FormArray, i: number): FormArray {
    return workstreams.at(i).get('groupWeights') as FormArray;
  }

  static getTotalGroupWeight(workstreams: FormArray, i: number): number {
    return this.getGroupWeightsArray(workstreams, i).controls.reduce((total, control) => {
      const weight = control.get('weight')?.value;
      return total + (weight ? Number.parseInt(weight, 10) : 0);
    }, 0);
  }

  static hasInvalidWorkstreamWeights(workstreams: FormArray): boolean {
    for (let i = 0; i < workstreams.length; i++) {
      const mode = workstreams.at(i).get('progressMode')?.value;
      if (mode === 'weight-based' && this.getGroupWeightsArray(workstreams, i).length > 0 && this.getTotalGroupWeight(workstreams, i) !== 100) {
        return true;
      }
    }
    return false;
  }

  // Auto: progress and group weights are all cleared (progress is computed as a plain average of the groups' progress).
  // Weight-based: progress is cleared, group weights are kept (progress is computed as a weighted average of the groups' progress).
  // Manual: group weights are cleared, progress is kept (as manually set by the user).
  static applyProgressMode(ws: any): void {
    if (ws.progressMode === 'weight-based') {
      ws.groupWeights = (ws.groupWeights || []).map(groupWeight => ({id: groupWeight.id, weight: groupWeight.weight}));
    } else {
      ws.groupWeights = (ws.groupWeights || []).map(groupWeight => ({id: groupWeight.id, weight: null}));
    }

    if (ws.progressMode !== 'manual') {
      ws.progress = null;
    }

    delete ws.roadmapGroupWeights;
  }

  static setProgressMode(form: FormGroup, mode: ProgressMode): void {
    form.get('progressMode').setValue(mode);
    if (mode === 'manual' && (form.get('progress')?.value == null || form.get('progress')?.value === '')) {
      form.get('progress')?.setValue(0);
    }
    form.updateValueAndValidity();
  }

  static setEventProgressMode(form: FormGroup, mode: EventProgressMode): void {
    this.setProgressMode(form, mode);
  }

  static getTotalWorkstreamWeight(workstreams: FormArray): number {
    return workstreams.controls.reduce((total, control) => {
      const weight = control.get('weight')?.value;
      return total + (weight ? Number.parseInt(weight, 10) : 0);
    }, 0);
  }

  static hasInvalidParentWorkstreamWeights(form: FormGroup, workstreams: FormArray): boolean {
    const mode = form.get('progressMode')?.value;
    return mode === 'weight-based' && workstreams.length > 0 && this.getTotalWorkstreamWeight(workstreams) !== 100;
  }

  // Same principle as applyProgressMode, but one level up: applied to the program/enabler form values instead of a workstream
  static applyParentProgressMode(formValues: any): void {
    if (formValues.progressMode !== 'weight-based') {
      (formValues.workstreams || []).forEach(ws => ws.weight = null);
    }

    if (formValues.progressMode !== 'manual') {
      formValues.progress = null;
    }
  }

  static getTotalEventWeight(eventWeights: FormArray): number {
    return eventWeights.controls.reduce((total, control) => {
      const weight = control.get('weight')?.value;
      return total + (weight ? Number.parseInt(weight, 10) : 0);
    }, 0);
  }

  static hasInvalidGroupEventWeights(form: FormGroup, eventWeights: FormArray): boolean {
    const mode = form.get('progressMode')?.value;
    return mode === 'weight-based' && eventWeights.length > 0 && this.getTotalEventWeight(eventWeights) !== 100;
  }

  static applyGroupProgressMode(formData: any): void {
    if (formData.progressMode === 'weight-based') {
      formData.eventWeights = (formData.eventWeights || []).map(eventWeight => ({id: eventWeight.id, weight: eventWeight.weight}));
    } else {
      formData.eventWeights = (formData.eventWeights || []).map(eventWeight => ({id: eventWeight.id, weight: null}));
    }

    if (formData.progressMode !== 'manual') {
      formData.progress = null;
    }
  }

  // Events only support 'auto' and 'manual' progress modes (no weight-based, as they have no children to weight).
  static getEventProgressMode(event: any): EventProgressMode {
    return event?.progress == null ? 'auto' : 'manual';
  }

  // Same convention as group/workstream/program/enabler progress modes, but events have no dedicated database
  // column for it: if progress is null, the event is in "auto" mode, otherwise it is in "manual" mode.
  static applyEventProgressMode(formData: any): void {
    if (formData.progressMode !== 'manual') {
      formData.progress = null;
    }
  }

  // Percentage progress of an event: its own manual progress if set, or, if not set (auto mode),
  // the percentage of the event's period (start date to end date) elapsed as of today.
  // Mirrors the backend computation in RoadmapUtil#computeEventPercentageProgress.
  static computeEventPercentageProgress(event: any): number {
    if (event?.progress != null) {
      return event.progress;
    }

    const startDate = moment(event?.startDate);
    const endDate = moment(event?.endDate);
    const today = moment();

    if (!today.isAfter(startDate, "day")) {
      return 0;
    }
    if (!today.isBefore(endDate, "day")) {
      return 100;
    }

    const totalDays = endDate.diff(startDate, "days");
    const elapsedDays = today.diff(startDate, "days");
    return Math.floor((elapsedDays * 100) / totalDays);
  }
}
